import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart2, TrendingUp, TrendingDown, AlertTriangle, DollarSign } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Area, AreaChart, ReferenceArea } from 'recharts';
import Layout from '../components/Layout';
import projectionService from '../services/projectionService';

export default function ProjectionsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['projections'],
    queryFn: () => projectionService.get(),
  });

  const projection = data?.projection || [];
  const futureData = projection.filter((p: any) => !p.isHistorical);
  const allData = projection;

  // Get current month label for highlighting
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const currentMonthLabel = new Date(currentYear, currentMonth - 1, 1).toLocaleDateString('pt-BR', { 
    month: 'short', 
    year: 'numeric' 
  });

  const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
  const formatShort = (v: number) => `${(v / 1000).toFixed(0)}k`;

  // Custom Tooltip to show if income is projected
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;
    
    const data = payload[0]?.payload;
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-semibold text-gray-900">{formatCurrency(entry.value)}</span>
            {entry.dataKey === 'income' && data?.incomeIsProjected && (
              <span className="text-xs text-amber-600 font-medium">(Projetado)</span>
            )}
          </div>
        ))}
        {data?.incomeIsProjected && (
          <p className="text-xs text-amber-700 mt-2 pt-2 border-t border-gray-200">
            ⚠️ Receita baseada no último registro
          </p>
        )}
      </div>
    );
  };

  // Calculate insights
  const criticalMonth = React.useMemo(() => {
    if (!futureData.length) return null;
    return [...futureData].sort((a, b) => b.expenses - a.expenses)[0];
  }, [futureData]);

  const negativeMonths = React.useMemo(() => {
    return futureData.filter((p: any) => p.balance < 0);
  }, [futureData]);

  const avgBalance = React.useMemo(() => {
    if (!futureData.length) return 0;
    return futureData.reduce((sum: number, p: any) => sum + p.balance, 0) / futureData.length;
  }, [futureData]);

  const totalProjectedIncome = React.useMemo(() => {
    return futureData.reduce((sum: number, p: any) => sum + p.income, 0);
  }, [futureData]);

  const totalProjectedExpenses = React.useMemo(() => {
    return futureData.reduce((sum: number, p: any) => sum + p.expenses, 0);
  }, [futureData]);

  return (
    <Layout title="Projeções Financeiras" icon={BarChart2}>
      <div className="flex flex-col h-full bg-gray-100">
        {/* Header - oculto em mobile */}
        <div className="hidden md:block bg-white border-b border-gray-200 px-4 md:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center">
              <BarChart2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Projeções Financeiras</h1>
              <p className="text-gray-600 text-sm mt-1">Análise de tendências e previsões para os próximos 12 meses</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-6">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Calculando projeções...</div>
          ) : projection.length > 0 ? (
            <>
              {/* Insight Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <p className="text-xs font-semibold text-gray-600">Receitas Projetadas (12m)</p>
                  </div>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(totalProjectedIncome)}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    <p className="text-xs font-semibold text-gray-600">Despesas Projetadas (12m)</p>
                  </div>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(totalProjectedExpenses)}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                    <p className="text-xs font-semibold text-gray-600">Saldo Médio Mensal</p>
                  </div>
                  <p className={`text-2xl font-bold ${avgBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(avgBalance)}
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                    <p className="text-xs font-semibold text-gray-600">Meses no Vermelho</p>
                  </div>
                  <p className={`text-2xl font-bold ${negativeMonths.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {negativeMonths.length} {negativeMonths.length === 1 ? 'mês' : 'meses'}
                  </p>
                </div>
              </div>

              {/* Critical Month Alert */}
              {criticalMonth && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900 text-sm">Mês Crítico Identificado</p>
                      <p className="text-sm text-amber-800 mt-1">
                        <strong>{criticalMonth.monthLabel}</strong> terá as maiores despesas projetadas:{' '}
                        <strong>{formatCurrency(criticalMonth.expenses)}</strong>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Line Chart */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-4 md:p-6">
                <div className="mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Evolução Mensal</h2>
                  <p className="text-sm text-gray-600">Histórico (3 meses) e projeção futura (12 meses)</p>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={allData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="monthLabel"
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        tickFormatter={formatShort}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend verticalAlign="top" height={36} />
                      {/* Highlight current month */}
                      {allData.map((item: any, idx: number) => 
                        item.monthLabel === currentMonthLabel ? (
                          <ReferenceArea
                            key={`current-${idx}`}
                            x1={idx > 0 ? allData[idx - 1].monthLabel : item.monthLabel}
                            x2={idx < allData.length - 1 ? allData[idx + 1].monthLabel : item.monthLabel}
                            fill="#3b82f6"
                            fillOpacity={0.15}
                            ifOverflow="extendDomain"
                          />
                        ) : null
                      )}
                      <Line
                        type="monotone"
                        dataKey="income"
                        name="Receitas"
                        stroke="#16a34a"
                        strokeWidth={2}
                        dot={(props: any) => {
                          const { cx, cy, payload } = props;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={payload.incomeIsProjected ? 4 : 3}
                              fill={payload.incomeIsProjected ? '#f59e0b' : '#16a34a'}
                              stroke={payload.incomeIsProjected ? '#d97706' : '#16a34a'}
                              strokeWidth={payload.incomeIsProjected ? 2 : 0}
                            />
                          );
                        }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="expenses"
                        name="Despesas"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="balance"
                        name="Saldo"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Area Chart - Category breakdown placeholder for future */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-4 md:p-6">
                <div className="mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Composição de Despesas (Área)</h2>
                  <p className="text-sm text-gray-600">Total de despesas projetadas mês a mês</p>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={allData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="monthLabel"
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        tickFormatter={formatShort}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend verticalAlign="top" height={36} />
                      {/* Highlight current month */}
                      {allData.map((item: any, idx: number) => 
                        item.monthLabel === currentMonthLabel ? (
                          <ReferenceArea
                            key={`current-area-${idx}`}
                            x1={idx > 0 ? allData[idx - 1].monthLabel : item.monthLabel}
                            x2={idx < allData.length - 1 ? allData[idx + 1].monthLabel : item.monthLabel}
                            fill="#3b82f6"
                            fillOpacity={0.15}
                            ifOverflow="extendDomain"
                          />
                        ) : null
                      )}
                      <Area
                        type="monotone"
                        dataKey="income"
                        name="Receitas"
                        stroke="#16a34a"
                        fill="#16a34a"
                        fillOpacity={0.3}
                      />
                      <Area
                        type="monotone"
                        dataKey="expenses"
                        name="Despesas"
                        stroke="#ef4444"
                        fill="#ef4444"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-100 p-6">
              <div className="text-center py-8 text-gray-500">
                Nenhuma projeção disponível. Cadastre fontes de renda, parcelas e contas recorrentes para gerar projeções.
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
